





















































































































































































import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";

import {
    TransportParty,
    TransportLoad,
    TransportLoadTask,
    TransportUnit,
    Waybill,
    WaybillRevision,
    PackagingType,
    WaybillRevisionItemCreateRequest,
} from "../models";

import {
  packagingTypeService,
    transportExecuteService,
    transportTaskTypeService,
    wasteCodeService,
    waybillManageService,
} from "../services";

import FindUnitDialog from "./FindUnitDialog.vue";
import UnitSelect from "./UnitSelect.vue";

interface Item {
    quantityUnits: string[];
    request: WaybillRevisionItemCreateRequest;
}

@Component({
    components: {
        FindUnitDialog,
        UnitSelect
    }
})
export default class TaskTransportingDialog extends Vue {
    @Prop() item!: { load: TransportLoad, loadTask: TransportLoadTask };
    @Prop() waybillUrl!: string;

    packagingTypes: PackagingType[] = [];

    waybill: null | Waybill = null;
    waybillItems: Item[] = [];

    get task() {
        return this.item.loadTask.task;
    }

    get revision(): null | WaybillRevision {
        if (!this.waybill) {
            return null;
        }

        return this.waybill.revisions[0];
    }

    get unitHandlingRequired() {
        return this.taskType && this.taskType.unitHandlingRequired;
    }

    state: "Loading" | "Waybill" | "LeaveUnits" | "TakeUnits" | "Confirm" = "Loading";

    taskType: { leaveUnits: boolean; takeUnits: boolean; internalTransfer: boolean; unitHandlingRequired: boolean; } = {
        leaveUnits: false,
        takeUnits: false,
        internalTransfer: false,
        unitHandlingRequired: false,
    };

    units: null | TransportUnit[] = null;
    vehicleRetreiveUnits: null | TransportUnit[] = null;

    leaveUnits: null | { options: TransportUnit[], value: null | TransportUnit[], party: TransportParty } = null;
    takeUnits: null | { options: TransportUnit[], value: null | TransportUnit[], party: TransportParty } = null;

    findUnitTarget: null | { options: TransportUnit[], value: TransportUnit[], party: TransportParty } = null;

    saving = false;
    unitsNotSpecifiedError = false;
    showConfirmLeaveUnitIgnoring = false;
    showConfirmTakeUnitIgnoring = false;
    externalLeaveUnit = false;
    externalTakeUnit = false;

    required(value: any) {
        return !!value || this.$t("validationErrors.required");
    }

    async mounted() {
        const result = await Promise.all([
            packagingTypeService.listPackagingTypes(),
            transportTaskTypeService.listTaskTypes(),
            transportExecuteService.listTaskAvailableUnits(this.task.taskId),
            transportExecuteService.listVehicleRetrieveUnits(this.item.load.vehicleId),
            waybillManageService.maybeCreateTaskWaybill(this.task.taskId),
        ]);

        this.packagingTypes = result[0];
        const taskTypes = result[1];
        this.units = result[2];
        this.vehicleRetreiveUnits = result[3];
        this.waybill = result[4];

        const taskType = taskTypes
            .find(tt => tt.taskTypeId === this.item.loadTask.task.taskType.taskTypeId);
        if (taskType) {
            this.taskType = taskType;
        }

        this.intoWaybillState();
    }

    async intoWaybillState(): Promise<void> {
        if (!this.task.waybillRequired || !this.waybill || !this.revision || this.revision.state !== "Draft") {
            this.intoUnitState();
            return;
        }

        const codes = await wasteCodeService.searchWasteCodes({
            wasteCode: this.revision.items.map(i => i.wasteCode),
        });

        const items = [];

        for (const item of this.revision.items) {
            const code = codes.find(c => c.code === item.wasteCode);

            items.push({
                quantityUnits: (code ? code.quantityUnits : null) || [item.quantityUnit],

                request: {
                    isGenerated: item.isGenerated,
                    isDeleted: item.isDeleted,
                    quantity: item.quantity,
                    quantityUnit: item.quantityUnit,
                    packagingTypeCode: item.packagingTypeCode,
                    packagingTypeDescription: item.packagingTypeDescription,

                    wasteCode: item.wasteCode,
                    description: item.description,

                    partDescription: item.partDescription,
                    unCode: item.unCode,
                    physicalStateCode: item.physicalStateCode,
                    physicalStateDescription: item.physicalStateDescription,
                    hazardClass: item.hazardClass,
                    rdCode: item.rdCode,
                },
            });
        }

        this.waybillItems = items;

        this.state = "Waybill";
    }

    waybillChanged(): boolean {
        for (let i = 0; i < this.waybillItems.length; ++i) {
            const src = this.revision!.items[i];
            const dst = this.waybillItems[i].request;

            if (src.quantity !== dst.quantity ||
                src.quantityUnit !== dst.quantityUnit ||
                src.packagingTypeCode !== dst.packagingTypeCode ||
                src.packagingTypeDescription !== dst.packagingTypeDescription) {
                    return true;
                }
        }

        return false;
    }

    async confirmWaybill(): Promise<void> {
        if (!(this.$refs.waybillForm as any).validate()) {
            return;
        }

        if (this.waybillChanged()) {
            try {
                this.saving = true;

                await waybillManageService.editWaybill(this.waybill!.waybillId, {
                    state: "Draft",
                    details: this.revision!.details,
                    items: this.waybillItems.map(i => i.request),
                });
            } finally {
                this.saving = false;
            }
        }

        this.intoUnitState();
    }

    getItemPackagingType(item: Item): null | PackagingType {
        return this.packagingTypes
            .find(t => t.code === item.request.packagingTypeCode && t.name === item.request.packagingTypeDescription) || null;
    }

    setItemPackagingType(item: Item, packagingType: PackagingType): void {
        if (!packagingType) {
            item.request.packagingTypeCode = null as any;
            item.request.packagingTypeDescription = null as any;
        } else {
            item.request.packagingTypeCode = packagingType.code;
            item.request.packagingTypeDescription = packagingType.name;
        }
    }

    intoUnitState() {
        const senderUnits = this.units!.filter(u => u.locationParty.partyId === this.task.senderParty.partyId);
        const receiverUnits = this.units!.filter(u => u.locationParty.partyId === this.task.receiverParty.partyId);

        const leaveUnits = this.taskType.leaveUnits ? [] : null;
        const takeUnits = this.taskType.takeUnits ? [] : null;

        // Deliver KK -> customer
        // - Leave units from sender (KK) to receiver (customer)
        // Exchange customer -> KK
        // - Leave units receiver (KK) to sender (customer)
        // - Take units from sender (customer) to receiver (KK)
        // Retrieve customer -> KK
        // - Take units from sender (customer) to receiver (KK)

        this.leaveUnits = this.task.direction === "Deliver"
            ? (this.taskType.internalTransfer
                ? { options: this.vehicleRetreiveUnits ? this.vehicleRetreiveUnits : [], value: leaveUnits, party: this.task.senderParty }
                : { options: senderUnits, value: leaveUnits, party: this.task.receiverParty })
            : { options: receiverUnits, value: leaveUnits, party: this.task.senderParty };

        this.takeUnits = this.task.direction === "Deliver"
            ? (this.taskType.internalTransfer
                ? { options: senderUnits, value: takeUnits, party: this.task.receiverParty }
                : { options: receiverUnits, value: takeUnits, party: this.task.senderParty })
            : { options: senderUnits, value: takeUnits, party: this.task.receiverParty };

        if (!this.leaveUnits!.value && !this.takeUnits!.value) {
            this.state = "Confirm";
        } else {
            this.state = "LeaveUnits";
        }
    }

    leaveUnitsNext() {
        const leaveUnits = this.leaveUnits!.value ? this.leaveUnits!.value : [];
        if (this.unitHandlingRequired && this.taskType.leaveUnits && leaveUnits.length < 1) {
            this.unitsNotSpecifiedError = true;
            return;
        }
        this.state = "TakeUnits";
    }

    ignoreLeaveUnits() {
        this.showConfirmLeaveUnitIgnoring = false;
        this.externalLeaveUnit = true;
        this.state = "TakeUnits";
    }

    takeUnitsNext() {
        const takeUnits = this.takeUnits!.value ? this.takeUnits!.value : [];
        if (this.unitHandlingRequired && this.taskType.takeUnits && takeUnits.length < 1) {
            this.unitsNotSpecifiedError = true;
            return;
        }
        this.state = "Confirm";
    }

    ignoreTakeUnits() {
        this.showConfirmTakeUnitIgnoring = false;
        this.externalTakeUnit = true;
        this.state = "Confirm";
    }

    foundUnit(unit: TransportUnit) {
        if (!this.findUnitTarget) {
            return;
        }

        const optionIndex = this.findUnitTarget.options.findIndex(u => u.unitId === unit.unitId);
        if (optionIndex !== -1) {
            this.findUnitTarget.options.splice(optionIndex, 1);
        }

        this.findUnitTarget.options.splice(0, 0, unit);

        const valueIndex = this.findUnitTarget.value.findIndex(u => u.unitId === unit.unitId);
        if (valueIndex !== -1) {
            this.findUnitTarget.value.splice(valueIndex, 1);
        }

        this.findUnitTarget.value.splice(0, 0, unit);

        this.findUnitTarget = null;
    }

    async confirm() {
        this.saving = true;

        await transportExecuteService.transportingTask(this.task.taskId, {
            leaveUnits: (this.leaveUnits!.value || []).map(u => u.unitId),
            takeUnits: (this.takeUnits!.value || []).map(u => u.unitId),
            leaveUnitExternal: this.externalLeaveUnit,
            takeUnitExternal: this.externalTakeUnit
        });

        this.$emit("reload");
    }
}
